/**
 * v2.2.0 の評価データマッパー
 *
 * データ構造 (functions/src/prompts/v2.2.0/evaluationService.ts の buildAnalyticsEvaluationRecord と一致):
 * - evaluationVersion: "2.2.0"
 * - topic: { id, theme, background, genre, difficulty }
 * - questionType / claimType はルート直下
 * - input: { userOpinion, understanding? }
 * - topicContent: ルート直下（旧 topic.content 相当、任意）
 * - app_version / promptVersion
 * - llmModels: 軸ごとの採点モデル名（v2.2.0 で追加）
 * - result: v2.1.0 と同じネスト構造だが、improvement.improvements に priority が加わる
 *
 * バージョン分離ルール: 他バージョンのマッパーや型を import せず、v2.2.0 用に自前で持つ。
 */

import { DocumentData } from "firebase/firestore";
import {
  EvaluationListItem,
  EvaluationDetailV2_2_0,
  EvaluationLlmModelsV2_2_0,
  ImprovementV2_2,
  ImprovementPriorityV2_2,
  ScoredChecklistItemV2_2,
  QuestionType,
  ClaimType,
} from "../../types";
import { getTimestampSeconds } from "./utils";

const mapScoredChecklist = (rawChecklist: unknown): ScoredChecklistItemV2_2[] | undefined => {
  if (!Array.isArray(rawChecklist)) return undefined;
  return rawChecklist.map((rawItem) => ({
    item: rawItem?.item || "",
    reason: rawItem?.reason || "",
    point: typeof rawItem?.point === "number" ? rawItem.point : 0,
    maxPoint: typeof rawItem?.maxPoint === "number" ? rawItem.maxPoint : 0,
  }));
};

// priority は v2.2.0 で追加された必須フィールド。想定外の値は medium に寄せる
const mapImprovementPriority = (rawPriority: unknown): ImprovementPriorityV2_2 => {
  if (rawPriority === "high" || rawPriority === "low") return rawPriority;
  return "medium";
};

const mapImprovements = (rawImprovements: unknown): ImprovementV2_2[] => {
  if (!Array.isArray(rawImprovements)) return [];
  return rawImprovements.map((rawImprovement) => ({
    title: rawImprovement?.title || "",
    scope: rawImprovement?.scope === "structural" ? "structural" : "partial",
    priority: mapImprovementPriority(rawImprovement?.priority),
    targetQuote: rawImprovement?.targetQuote ?? null,
    explanation: rawImprovement?.explanation || "",
    rewriteExample: rawImprovement?.rewriteExample ?? null,
  }));
};

const mapLlmModels = (rawLlmModels: unknown): EvaluationLlmModelsV2_2_0 | undefined => {
  if (typeof rawLlmModels !== "object" || rawLlmModels === null) return undefined;
  const models = rawLlmModels as Record<string, unknown>;
  return {
    understandingScoring:
      typeof models.understandingScoring === "string" ? models.understandingScoring : "",
    logicOriginalityScoring:
      typeof models.logicOriginalityScoring === "string" ? models.logicOriginalityScoring : "",
    structureExpressionScoring:
      typeof models.structureExpressionScoring === "string"
        ? models.structureExpressionScoring
        : "",
    improvement: typeof models.improvement === "string" ? models.improvement : "",
  };
};

/**
 * v2.2.0データを一覧アイテムに変換
 */
export function mapV2_2_0ToListItem(docId: string, data: DocumentData): EvaluationListItem {
  const userOpinion = data.input?.userOpinion || "";
  const theme = data.topic?.theme || "";
  const topicContent = data.topicContent || theme;

  return {
    id: docId,
    version: "v2.2.0",
    timestamp: { _seconds: getTimestampSeconds(data.timestamp) },
    topic: {
      id: data.topic?.id || "",
      content: topicContent,
      genre: data.topic?.genre || "",
      difficulty: data.topic?.difficulty || 0,
      theme,
      questionType: (data.questionType as QuestionType) || undefined,
      claimType: (data.claimType as ClaimType) || undefined,
      background: data.topic?.background || undefined,
    },
    userOpinionPreview:
      userOpinion.length > 100 ? userOpinion.substring(0, 100) + "..." : userOpinion,
    totalScore: data.result?.totalScore || 0,
    appVersion: data.app_version || "",
  };
}

/**
 * v2.2.0データを詳細に変換
 */
export function mapV2_2_0ToDetail(docId: string, data: DocumentData): EvaluationDetailV2_2_0 {
  const resultData = data.result || {};
  const understandingBlock = resultData.understanding || {};
  const logicOriginalityBlock = resultData.logicOriginality || {};
  const structureExpressionBlock = resultData.structureExpression || {};
  const improvementBlock = resultData.improvement || {};

  const understanding =
    data.input?.understanding != null
      ? {
          claim: data.input.understanding.claim || "",
          grounds: data.input.understanding.grounds || "",
        }
      : undefined;

  return {
    id: docId,
    version: "v2.2.0",
    timestamp: { _seconds: getTimestampSeconds(data.timestamp) },
    topic: {
      id: data.topic?.id || "",
      theme: data.topic?.theme || "",
      background: data.topic?.background || undefined,
      genre: data.topic?.genre || "",
      difficulty: data.topic?.difficulty || 0,
      content: data.topicContent || undefined,
      questionType: (data.questionType as QuestionType) || undefined,
      claimType: (data.claimType as ClaimType | null) ?? undefined,
    },
    input: {
      userOpinion: data.input?.userOpinion || "",
      understanding,
    },
    result: {
      understanding: {
        scores: {
          understanding: understandingBlock.scores?.understanding || 0,
        },
        scoreExplanations: {
          understanding: understandingBlock.scoreExplanations?.understanding || "",
        },
        reasoning: {
          understandingScoredChecklist: mapScoredChecklist(
            understandingBlock.reasoning?.understandingScoredChecklist
          ),
        },
      },
      logicOriginality: {
        scores: {
          logic: logicOriginalityBlock.scores?.logic || 0,
          originality: logicOriginalityBlock.scores?.originality || 0,
        },
        scoreExplanations: {
          logic: logicOriginalityBlock.scoreExplanations?.logic || "",
          originality: logicOriginalityBlock.scoreExplanations?.originality || "",
        },
        reasoning: {
          logicScoredChecklist: mapScoredChecklist(
            logicOriginalityBlock.reasoning?.logicScoredChecklist
          ),
          originalityScoredChecklist: mapScoredChecklist(
            logicOriginalityBlock.reasoning?.originalityScoredChecklist
          ),
        },
      },
      structureExpression: {
        scores: {
          structure: structureExpressionBlock.scores?.structure || 0,
          expression: structureExpressionBlock.scores?.expression || 0,
        },
        scoreExplanations: {
          structure: structureExpressionBlock.scoreExplanations?.structure || "",
          expression: structureExpressionBlock.scoreExplanations?.expression || "",
        },
        reasoning: {
          structureScoredChecklist: mapScoredChecklist(
            structureExpressionBlock.reasoning?.structureScoredChecklist
          ),
          expressionScoredChecklist: mapScoredChecklist(
            structureExpressionBlock.reasoning?.expressionScoredChecklist
          ),
        },
      },
      improvement: {
        shortReview: improvementBlock.shortReview || "",
        improvements: mapImprovements(improvementBlock.improvements),
        strengths: improvementBlock.strengths || [],
        improvedExample: improvementBlock.improvedExample || "",
      },
      questionType: (resultData.questionType as QuestionType) || undefined,
      claimType: (resultData.claimType as ClaimType | null) ?? undefined,
      totalScore: resultData.totalScore || 0,
    },
    appVersion: data.app_version || "",
    promptVersion: typeof data.promptVersion === "number" ? data.promptVersion : undefined,
    llmModels: mapLlmModels(data.llmModels),
  };
}
